/**
 * PersonalkostenPage — Monatliche PLAN/IST Personalkosten pro Mitarbeiter.
 *
 * 3 Tabs: Monatliche Erfassung | Jahresplanung | Übersicht
 *
 * @page /personalkosten
 * @roles administrator, ops_manager
 * @since v2.189.0
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, ChevronLeft, ChevronRight, Download, RefreshCw, TrendingUp, TrendingDown, Users, AlertCircle, CheckCircle, Info, Wand2, Activity, ArrowUpDown, Trash2, Upload, FileText, X, Check, Settings } from 'lucide-react';
import useTabParam from '../hooks/useTabParam';
import ModulEinstellungen from '../components/ModulEinstellungen';
import { useLanguage } from '../context/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart, LineChart, Area, AreaChart } from 'recharts';

const api = (path, opts) => fetch(path, { credentials: 'include', ...opts }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
const apiPut = (path, body) => api(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiPost = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const apiDelete = (path) => api(path, { method: 'DELETE' });
const fmt = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';
const fmtPct = (n) => n != null ? `${n > 0 ? '+' : ''}${Number(n).toFixed(1)}%` : '—';
const MONAT_NAMEN = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

// ─── KPI Mini Card ───
function Kpi({ label, value, sub, trend }) {
  return (
    <div style={{ flex: 1, minWidth: 160, padding: '16px 20px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary,#1A1A2E)' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: trend === 'up' ? '#EF4444' : trend === 'down' ? '#22C55E' : 'var(--text-tertiary,#6B7280)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
        {trend === 'up' && <TrendingUp size={12} />}
        {trend === 'down' && <TrendingDown size={12} />}
        {sub}
      </div>}
    </div>
  );
}

// ─── Tab Bar ───
function TabBar({ tabs, active, onChange, t }) {
  return (
    <div style={{ display: 'flex', gap: 2, background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10, padding: 3, border: '1px solid var(--border-card,#E5E7EB)', marginBottom: 20, width: 'fit-content' }}>
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => onChange(tab.key)}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active === tab.key ? 600 : 400,
            background: active === tab.key ? 'var(--bg-card,#fff)' : 'transparent',
            boxShadow: active === tab.key ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            color: active === tab.key ? 'var(--text-primary,#1A1A2E)' : 'var(--text-tertiary,#6B7280)' }}>
          {t(tab.labelKey)}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════
//  MONITOR TAB — PLAN vs IST Analyse
// ═══════════════════════════════════════
function MonitorTab({ t }) {
  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [monat, setMonat] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [sim, setSim] = useState(null);
  const [simProd, setSimProd] = useState(75);
  const [sortCol, setSortCol] = useState('auslastungsgrad');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api(`/api/personalkosten/monitor?jahr=${jahr}&monat=${monat}`),
      api(`/api/personalkosten/monitor/trend?monate=6`),
    ]).then(([d, tr]) => { setData(d); setTrend(tr.trend || []); }).catch(() => {}).finally(() => setLoading(false));
  }, [jahr, monat]);

  useEffect(() => { load(); }, [load]);

  // Simulation
  useEffect(() => {
    const timer = setTimeout(() => {
      api(`/api/personalkosten/monitor/simulation?jahr=${jahr}&monat=${monat}&produktivitaet=${simProd / 100}`)
        .then(setSim).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [jahr, monat, simProd]);

  const prevMonth = () => { if (monat === 1) { setJahr(j => j - 1); setMonat(12); } else setMonat(m => m - 1); };
  const nextMonth = () => { if (monat === 12) { setJahr(j => j + 1); setMonat(1); } else setMonat(m => m + 1); };

  const kpis = data?.kpis || {};
  const rows = data?.rows || [];
  const fmtH = (n) => n != null ? `${Number(n).toFixed(1)}h` : '—';
  const fmtEur = (n) => n != null ? `€${fmt(n)}` : '—';

  // Sort rows
  const sortedRows = [...rows].filter(r => r.hat_daten).sort((a, b) => {
    const va = a[sortCol] ?? -Infinity;
    const vb = b[sortCol] ?? -Infinity;
    return sortDir === 'asc' ? va - vb : vb - va;
  });
  const noDataRows = rows.filter(r => !r.hat_daten);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortHead = ({ col, children, style }) => (
    <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => handleSort(col)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        {sortCol === col && <ArrowUpDown size={10} style={{ opacity: 0.6 }} />}
      </span>
    </th>
  );

  // Row color based on Auslastung
  const rowBg = (ausl) => {
    if (ausl >= 70) return 'rgba(34,197,94,0.04)';
    if (ausl >= 50) return 'transparent';
    if (ausl >= 30) return 'rgba(251,191,36,0.04)';
    return 'rgba(239,68,68,0.04)';
  };

  return (
    <div>
      {/* Month Navigator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={prevMonth} style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', minWidth: 120, textAlign: 'center' }}>
          {MONAT_NAMEN[monat]} {jahr}
        </div>
        <button onClick={nextMonth} style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <ChevronRight size={16} />
        </button>
        {loading && <RefreshCw size={16} style={{ color: 'var(--text-muted,#9CA3AF)', animation: 'spin 1s linear infinite' }} />}
      </div>

      {/* 6 KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label={t('pk.monPlanStundensatz')} value={kpis.plan_stundensatz != null ? `€${kpis.plan_stundensatz}` : '—'} sub={t('pk.monProStunde')} />
        <Kpi label={t('pk.monIstStundensatz')} value={kpis.ist_stundensatz != null ? `€${kpis.ist_stundensatz}` : '—'}
          sub={kpis.plan_stundensatz && kpis.ist_stundensatz ? `${kpis.ist_stundensatz > kpis.plan_stundensatz ? '+' : ''}${Math.round((kpis.ist_stundensatz - kpis.plan_stundensatz) / kpis.plan_stundensatz * 100)}% vs PLAN` : null}
          trend={kpis.ist_stundensatz > kpis.plan_stundensatz ? 'up' : 'down'} />
        <Kpi label={t('pk.monAuslastung')} value={`${kpis.auslastungsgrad || 0}%`}
          sub={`${fmtH(kpis.productive_stunden)} / ${fmtH(kpis.soll_stunden)}`}
          trend={kpis.auslastungsgrad >= 60 ? 'down' : 'up'} />
        <Kpi label={t('pk.monNutzkosten')} value={fmtEur(kpis.nutzkosten)} sub={t('pk.monProduktiv')} />
        <Kpi label={t('pk.monLeerkosten')} value={fmtEur(kpis.leerkosten)}
          sub={kpis.nutzkosten && kpis.leerkosten ? `${Math.round(kpis.leerkosten / (kpis.nutzkosten + kpis.leerkosten) * 100)}% ${t('pk.monVomGesamt')}` : null}
          trend={kpis.leerkosten > kpis.nutzkosten * 0.3 ? 'up' : null} />
        <Kpi label={t('pk.monKostenPaket')} value={kpis.kosten_pro_paket != null ? `€${kpis.kosten_pro_paket}` : '—'}
          sub={`${fmt(kpis.pakete_gesamt)} ${t('pk.monPakete')}`} />
      </div>

      {/* Explanation Block */}
      <div style={{ padding: '12px 16px', borderRadius: 10, border: '1px solid #DBEAFE', background: '#EFF6FF', marginBottom: 20, fontSize: 12, color: '#1E40AF', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Info size={14} style={{ marginTop: 1, flexShrink: 0 }} />
        <div>
          <strong>{t('pk.monExplainTitle')}</strong><br />
          {t('pk.monExplainText')}
        </div>
      </div>

      {/* Sortable MA Table */}
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, overflow: 'auto', background: 'var(--bg-card,#fff)', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-inset,#F8F9FC)' }}>
              <th style={{ ...thStyle, textAlign: 'left' }}>{t('pk.mitarbeiter')}</th>
              <SortHead col="soll_stunden">{t('pk.monSoll')}</SortHead>
              <SortHead col="productive_stunden">{t('pk.monProduktivH')}</SortHead>
              <SortHead col="idle_stunden">{t('pk.monIdleH')}</SortHead>
              <SortHead col="auslastungsgrad" style={{ borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>{t('pk.monAuslCol')}</SortHead>
              <SortHead col="plan_cost">PLAN €</SortHead>
              <SortHead col="plan_stundensatz">{t('pk.monPlanRate')}</SortHead>
              <SortHead col="ist_stundensatz">{t('pk.monIstRate')}</SortHead>
              <SortHead col="nutzkosten">{t('pk.monNutzCol')}</SortHead>
              <SortHead col="leerkosten">{t('pk.monLeerCol')}</SortHead>
              <SortHead col="pakete">{t('pk.monPaketeCol')}</SortHead>
              <SortHead col="kosten_pro_paket">€/{t('pk.monPkg')}</SortHead>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(r => (
              <tr key={r.mitarbeiter_id} style={{ borderTop: '1px solid var(--border-card,#E5E7EB)', background: rowBg(r.auslastungsgrad) }}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 500 }}>
                  {r.name}
                  {r.lager_rolle && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-muted,#9CA3AF)', textTransform: 'uppercase' }}>{r.lager_rolle}</span>}
                </td>
                <td style={tdStyle}>{fmtH(r.soll_stunden)}</td>
                <td style={tdStyle}>{fmtH(r.productive_stunden)}</td>
                <td style={{ ...tdStyle, color: r.idle_stunden > 20 ? '#EF4444' : 'var(--text-tertiary,#6B7280)' }}>{fmtH(r.idle_stunden)}</td>
                <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)', fontWeight: 600, color: 'var(--text-primary,#1A1A2E)' }}>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
                    background: r.auslastungsgrad >= 60 ? '#F0FDF4' : r.auslastungsgrad >= 30 ? '#FFFBEB' : '#FEF2F2',
                    color: r.auslastungsgrad >= 60 ? '#15803D' : r.auslastungsgrad >= 30 ? '#B45309' : '#B91C1C' }}>
                    {r.auslastungsgrad}%
                  </span>
                </td>
                <td style={tdStyle}>{r.plan_cost != null ? fmtEur(r.plan_cost) : '—'}</td>
                <td style={{ ...tdStyle, color: 'var(--text-tertiary,#6B7280)' }}>{r.plan_stundensatz != null ? `€${r.plan_stundensatz}` : '—'}</td>
                <td style={{ ...tdStyle, fontWeight: 500, color: r.ist_stundensatz && r.plan_stundensatz && r.ist_stundensatz > r.plan_stundensatz * 1.5 ? '#EF4444' : 'var(--text-primary,#1A1A2E)' }}>
                  {r.ist_stundensatz != null ? `€${r.ist_stundensatz}` : '—'}
                </td>
                <td style={{ ...tdStyle, color: 'var(--text-primary,#1A1A2E)' }}>{r.nutzkosten != null ? fmtEur(r.nutzkosten) : '—'}</td>
                <td style={{ ...tdStyle, color: 'var(--text-primary,#1A1A2E)' }}>{r.leerkosten != null ? fmtEur(r.leerkosten) : '—'}</td>
                <td style={tdStyle}>{fmt(r.pakete)}</td>
                <td style={{ ...tdStyle, fontWeight: 500 }}>{r.kosten_pro_paket != null ? `€${r.kosten_pro_paket}` : '—'}</td>
              </tr>
            ))}
            {noDataRows.length > 0 && (
              <tr style={{ borderTop: '2px solid var(--border-card,#E5E7EB)' }}>
                <td colSpan={12} style={{ ...tdStyle, textAlign: 'left', color: 'var(--text-muted,#9CA3AF)', fontStyle: 'italic' }}>
                  {noDataRows.length} MA {t('pk.monNoDaten')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* IST vs PLAN Stundensatz Trend */}
        <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>{t('pk.monChartStundensatz')}</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `€${v}`} />
              <Tooltip formatter={(v) => `€${v}`} />
              <Legend />
              <Line type="monotone" dataKey="plan_stundensatz" name="PLAN €/h" stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="ist_stundensatz" name="IST €/h" stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Nutzkosten vs Leerkosten Stacked Bar */}
        <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>{t('pk.monChartNutzLeer')}</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `${Math.round(v/1000)}k`} />
              <Tooltip formatter={(v) => `€${fmt(v)}`} />
              <Legend />
              <Bar dataKey="nutzkosten" name={t('pk.monNutzCol')} stackId="a" fill="#338edc" radius={[0,0,0,0]} />
              <Bar dataKey="leerkosten" name={t('pk.monLeerCol')} stackId="a" fill="#E5E7EB" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Kosten/Paket Trend + Simulation */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Kosten/Paket Trend */}
        <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>{t('pk.monChartCpp')}</div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={trend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `€${v}`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `${Math.round(v/1000)}k`} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="kosten_pro_paket" name="€/Paket" stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
              <Bar yAxisId="right" dataKey="pakete" name={t('pk.monPakete')} fill="#E5E7EB" radius={[4,4,0,0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Simulation */}
        <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>{t('pk.monSimTitle')}</div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)' }}>{t('pk.monSimTarget')}</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#338edc' }}>{simProd}%</span>
            </div>
            <input type="range" min={30} max={95} value={simProd} onChange={e => setSimProd(parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#338edc' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted,#9CA3AF)' }}>
              <span>30%</span><span>95%</span>
            </div>
          </div>
          {sim && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted,#9CA3AF)', marginBottom: 4 }}>{t('pk.monSimAktuell')}</div>
                <div style={{ fontSize: 12 }}>
                  <div style={{ marginBottom: 2 }}>{t('pk.monAuslCol')}: <strong>{sim.aktuell.auslastungsgrad}%</strong></div>
                  <div style={{ marginBottom: 2 }}>€/h: <strong>€{sim.aktuell.ist_stundensatz}</strong></div>
                  <div style={{ marginBottom: 2 }}>€/{t('pk.monPkg')}: <strong>€{sim.aktuell.kosten_pro_paket}</strong></div>
                  <div style={{ color: '#EF4444' }}>{t('pk.monLeerCol')}: <strong>€{fmt(sim.aktuell.leerkosten)}</strong></div>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#338edc', marginBottom: 4, fontWeight: 600 }}>{t('pk.monSimZiel')} {simProd}%</div>
                <div style={{ fontSize: 12 }}>
                  <div style={{ marginBottom: 2 }}>{t('pk.monAuslCol')}: <strong>{sim.simulation.auslastungsgrad}%</strong></div>
                  <div style={{ marginBottom: 2 }}>€/h: <strong>€{sim.simulation.ist_stundensatz}</strong></div>
                  <div style={{ marginBottom: 2 }}>€/{t('pk.monPkg')}: <strong>€{sim.simulation.kosten_pro_paket}</strong></div>
                  <div style={{ color: '#22C55E' }}>{t('pk.monLeerCol')}: <strong>€{fmt(sim.simulation.leerkosten)}</strong></div>
                </div>
              </div>
              {sim.simulation.einsparung_leerkosten > 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '8px 12px', borderRadius: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', textAlign: 'center', fontSize: 13 }}>
                  <span style={{ color: '#065F46', fontWeight: 600 }}>
                    {t('pk.monSimEinsparung')}: €{fmt(sim.simulation.einsparung_leerkosten)}/Mo
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════
//  MONATLICHE ERFASSUNG TAB
// ═══════════════════════════════════════
function MonatTab({ t }) {
  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [monat, setMonat] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showFormer, setShowFormer] = useState(false);

  const load = useCallback(() => {
    const formerParam = showFormer ? '&include_former=true' : '';
    api(`/api/personalkosten/monat?jahr=${jahr}&monat=${monat}${formerParam}`).then(setData).catch(() => {});
  }, [jahr, monat, showFormer]);

  useEffect(() => { load(); setEdits({}); }, [load]);

  const prevMonth = () => {
    if (monat === 1) { setJahr(j => j - 1); setMonat(12); }
    else setMonat(m => m - 1);
  };
  const nextMonth = () => {
    if (monat === 12) { setJahr(j => j + 1); setMonat(1); }
    else setMonat(m => m + 1);
  };

  const handleChange = (maId, field, value) => {
    setEdits(prev => ({
      ...prev,
      [maId]: { ...(prev[maId] || {}), [field]: value === '' ? null : parseFloat(value) }
    }));
  };

  const handleSave = async () => {
    const entries = Object.entries(edits).map(([maId, fields]) => ({
      mitarbeiter_id: parseInt(maId),
      ...fields
    }));
    if (!entries.length) return;
    setSaving(true);
    try {
      await apiPost('/api/personalkosten/monat/bulk', { jahr, monat, entries });
      setToast(t('pk.saved'));
      setEdits({});
      load();
      setTimeout(() => setToast(''), 2500);
    } catch {
      setToast(t('pk.saveError'));
      setTimeout(() => setToast(''), 3000);
    }
    setSaving(false);
  };

  const handleDelete = async (mkId) => {
    try {
      await apiDelete(`/api/personalkosten/monatlich/${mkId}`);
      setToast(t('pk.deleted'));
      setConfirmDeleteId(null);
      load();
      setTimeout(() => setToast(''), 2500);
    } catch {
      setToast(t('pk.deleteError'));
      setTimeout(() => setToast(''), 3000);
    }
  };

  const hasEdits = Object.keys(edits).length > 0;
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div>
      {/* Delete Confirm Dialog */}
      {confirmDeleteId && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}
          onClick={() => setConfirmDeleteId(null)}>
          <div style={{ background: 'var(--bg-card,#fff)', borderRadius: 12, padding: 24, maxWidth: 400, width: '90%', border: '1px solid var(--border-card,#E5E7EB)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8, color: 'var(--text-primary,#1A1A2E)' }}>{t('pk.deleteConfirmTitle')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary,#4B5563)', marginBottom: 16 }}>{t('pk.deleteConfirmMsg')}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDeleteId(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', fontSize: 13, cursor: 'pointer' }}>
                {t('common.cancel')}
              </button>
              <button onClick={() => handleDelete(confirmDeleteId)}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#EF4444', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {t('pk.deleteBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Month Navigator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={prevMonth} style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <ChevronLeft size={16} />
        </button>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', minWidth: 120, textAlign: 'center' }}>
          {MONAT_NAMEN[monat]} {jahr}
        </div>
        <button onClick={nextMonth} style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <ChevronRight size={16} />
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={showFormer} onChange={e => setShowFormer(e.target.checked)}
            style={{ width: 14, height: 14 }} />
          <span style={{ fontSize: 12, color: 'var(--text-secondary,#4B5563)' }}>{t('mitarbeiter.zeigeEhemalige')}</span>
        </label>
        <div style={{ flex: 1 }} />
        {hasEdits && <button onClick={handleSave} disabled={saving}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#338edc', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
          <Save size={14} /> {saving ? '...' : t('pk.save')}
        </button>}
      </div>

      {/* KPI Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Kpi label={t('pk.planGesamt')} value={`€${fmt(totals.plan_gesamt)}`} sub={`${totals.ma_mit_plan || 0}/${totals.ma_count || 0} MA`} />
        <Kpi label={t('pk.istGesamt')} value={`€${fmt(totals.ist_gesamt)}`} sub={`${totals.ma_mit_ist || 0}/${totals.ma_count || 0} MA`} />
        <Kpi label={t('pk.diff')} value={totals.diff != null ? `€${fmt(totals.diff)}` : '—'}
          sub={totals.diff_pct != null ? fmtPct(totals.diff_pct) : null}
          trend={totals.diff > 0 ? 'up' : totals.diff < 0 ? 'down' : null} />
      </div>

      {/* Table */}
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, overflow: 'auto', background: 'var(--bg-card,#fff)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-inset,#F8F9FC)' }}>
              <th style={thStyle}>{t('pk.mitarbeiter')}</th>
              <th style={thStyle}>{t('pk.hWoche')}</th>
              <th style={{ ...thStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>{t('pk.planBrutto')}</th>
              <th style={thStyle}>{t('pk.planAg')}</th>
              <th style={thStyle}>{t('pk.planSonder')}</th>
              <th style={thStyle}>{t('pk.planTotal')}</th>
              <th style={{ ...thStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>{t('pk.istBrutto')}</th>
              <th style={thStyle}>{t('pk.istAg')}</th>
              <th style={thStyle}>{t('pk.istSonder')}</th>
              <th style={thStyle}>{t('pk.istTotal')}</th>
              <th style={{ ...thStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>{t('pk.diffCol')}</th>
              <th style={thStyle}>%</th>
              <th style={{ ...thStyle, width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const e = edits[r.mitarbeiter_id] || {};
              const planBrutto = e.brutto_gehalt !== undefined ? e.brutto_gehalt : r.plan_brutto;
              const planAg = e.ag_nebenkosten !== undefined ? e.ag_nebenkosten : r.plan_ag;
              const planSonder = e.sonderzahlungen !== undefined ? e.sonderzahlungen : r.plan_sonder;
              const planTotal = (planBrutto || 0) + (planAg || 0) + (planSonder || 0);
              const istBrutto = e.ist_brutto !== undefined ? e.ist_brutto : r.ist_brutto;
              const istAg = e.ist_ag_nebenkosten !== undefined ? e.ist_ag_nebenkosten : r.ist_ag;
              const istSonder = e.ist_sonderzahlungen !== undefined ? e.ist_sonderzahlungen : r.ist_sonder;
              const istTotal = istBrutto != null ? (istBrutto || 0) + (istAg || 0) + (istSonder || 0) : null;
              const diff = planTotal && istTotal != null ? istTotal - planTotal : null;
              const diffPct = planTotal > 0 && istTotal != null ? ((istTotal - planTotal) / planTotal * 100) : null;
              const edited = Object.keys(e).length > 0;

              return (
                <tr key={r.mitarbeiter_id} style={{ borderTop: '1px solid var(--border-card,#E5E7EB)', background: edited ? 'rgba(51,142,220,0.03)' : 'transparent' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>
                    {r.name}
                    {r.plan_quelle === 'jahresplanung' && <span title={t('pk.fromJahresplanung')} style={{ marginLeft: 4, color: 'var(--text-muted,#9CA3AF)', fontSize: 10 }}>JP</span>}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-tertiary,#6B7280)' }}>{r.wochenstunden_soll}h</td>
                  <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>
                    <NumInput value={planBrutto} onChange={v => handleChange(r.mitarbeiter_id, 'brutto_gehalt', v)} />
                  </td>
                  <td style={tdStyle}>
                    <NumInput value={planAg} onChange={v => handleChange(r.mitarbeiter_id, 'ag_nebenkosten', v)} />
                  </td>
                  <td style={tdStyle}>
                    <NumInput value={planSonder} onChange={v => handleChange(r.mitarbeiter_id, 'sonderzahlungen', v)} placeholder="0" />
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)' }}>{planTotal > 0 ? `€${fmt(planTotal)}` : '—'}</td>
                  <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>
                    <NumInput value={istBrutto} onChange={v => handleChange(r.mitarbeiter_id, 'ist_brutto', v)} />
                  </td>
                  <td style={tdStyle}>
                    <NumInput value={istAg} onChange={v => handleChange(r.mitarbeiter_id, 'ist_ag_nebenkosten', v)} />
                  </td>
                  <td style={tdStyle}>
                    <NumInput value={istSonder} onChange={v => handleChange(r.mitarbeiter_id, 'ist_sonderzahlungen', v)} placeholder="0" />
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)' }}>{istTotal != null ? `€${fmt(istTotal)}` : '—'}</td>
                  <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)', fontWeight: 500, color: diff > 0 ? '#EF4444' : diff < 0 ? '#22C55E' : 'var(--text-primary,#1A1A2E)' }}>
                    {diff != null ? `€${fmt(diff)}` : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: diffPct > 5 ? '#EF4444' : diffPct < -5 ? '#22C55E' : 'var(--text-tertiary,#6B7280)' }}>
                    {diffPct != null ? fmtPct(diffPct) : ''}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', padding: '4px 6px' }}>
                    {r.mk_id && (
                      <button onClick={() => setConfirmDeleteId(r.mk_id)} title={t('pk.deleteRow')}
                        style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted,#9CA3AF)', borderRadius: 4 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted,#9CA3AF)'}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {/* Footer */}
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border-card,#E5E7EB)', background: 'var(--bg-inset,#F8F9FC)', fontWeight: 600 }}>
              <td style={tdStyle}>{t('pk.total')}</td>
              <td style={tdStyle} />
              <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }} />
              <td style={tdStyle} />
              <td style={tdStyle} />
              <td style={tdStyle}>€{fmt(totals.plan_gesamt)}</td>
              <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }} />
              <td style={tdStyle} />
              <td style={tdStyle} />
              <td style={tdStyle}>€{fmt(totals.ist_gesamt)}</td>
              <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)', color: totals.diff > 0 ? '#EF4444' : totals.diff < 0 ? '#22C55E' : 'var(--text-primary,#1A1A2E)' }}>
                €{fmt(totals.diff)}
              </td>
              <td style={{ ...tdStyle, fontSize: 12, color: totals.diff_pct > 0 ? '#EF4444' : totals.diff_pct < 0 ? '#22C55E' : 'var(--text-tertiary)' }}>
                {totals.diff_pct != null ? fmtPct(totals.diff_pct) : ''}
              </td>
              <td style={tdStyle} />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: toast.includes('Error') || toast.includes('Fehler') ? '#FEE2E2' : '#D1FAE5', color: toast.includes('Error') || toast.includes('Fehler') ? '#991B1B' : '#065F46', padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 1100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {toast}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════
//  JAHRESPLANUNG TAB
// ═══════════════════════════════════════
function JahresplanungTab({ t }) {
  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [prefilling, setPrefilling] = useState(false);

  const load = useCallback(() => {
    api(`/api/personalkosten/jahresplanung?jahr=${jahr}`).then(setData).catch(() => {});
  }, [jahr]);

  useEffect(() => { load(); setEdits({}); }, [load]);

  const handleChange = (maId, field, value) => {
    setEdits(prev => ({
      ...prev,
      [maId]: { ...(prev[maId] || {}), [field]: value === '' ? null : parseFloat(value) }
    }));
  };

  const handleSave = async () => {
    const entries = Object.entries(edits).map(([maId, fields]) => {
      const row = data?.rows?.find(r => r.mitarbeiter_id === parseInt(maId));
      return {
        mitarbeiter_id: parseInt(maId),
        monats_brutto: fields.monats_brutto !== undefined ? fields.monats_brutto : row?.monats_brutto,
        ag_nebenkosten_pct: fields.ag_nebenkosten_pct !== undefined ? fields.ag_nebenkosten_pct : row?.ag_nebenkosten_pct,
        sonderzahlungen_jahres: fields.sonderzahlungen_jahres !== undefined ? fields.sonderzahlungen_jahres : row?.sonderzahlungen_jahres
      };
    }).filter(e => e.monats_brutto != null);
    if (!entries.length) return;
    setSaving(true);
    try {
      await apiPost('/api/personalkosten/jahresplanung/bulk', { jahr, entries });
      setToast(t('pk.saved'));
      setEdits({});
      load();
      setTimeout(() => setToast(''), 2500);
    } catch {
      setToast(t('pk.saveError'));
      setTimeout(() => setToast(''), 3000);
    }
    setSaving(false);
  };

  const handlePrefill = async (quelle) => {
    setPrefilling(true);
    try {
      const res = await apiPost('/api/personalkosten/jahresplanung/prefill', { jahr, quelle });
      if (res.ok) {
        setToast(`${res.count} ${t('pk.prefilled')}`);
        load();
      } else {
        setToast(res.message || t('pk.saveError'));
      }
      setTimeout(() => setToast(''), 3000);
    } catch {
      setToast(t('pk.saveError'));
      setTimeout(() => setToast(''), 3000);
    }
    setPrefilling(false);
  };

  const hasEdits = Object.keys(edits).length > 0;
  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={jahr} onChange={e => setJahr(parseInt(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', fontSize: 14, color: 'var(--text-primary,#1A1A2E)' }}>
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        {totals.ma_mit_planung === 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handlePrefill('stundensatz')} disabled={prefilling}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', fontSize: 13, cursor: 'pointer', color: 'var(--text-primary,#1A1A2E)', display: 'flex', alignItems: 'center', gap: 6, opacity: prefilling ? 0.6 : 1 }}>
              <Wand2 size={14} /> {t('pk.prefillStundensatz')}
            </button>
            <button onClick={() => handlePrefill('vorjahr')} disabled={prefilling}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', fontSize: 13, cursor: 'pointer', color: 'var(--text-primary,#1A1A2E)', display: 'flex', alignItems: 'center', gap: 6, opacity: prefilling ? 0.6 : 1 }}>
              <RefreshCw size={14} /> {t('pk.prefillVorjahr')}
            </button>
          </div>
        )}
        {hasEdits && <button onClick={handleSave} disabled={saving}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#338edc', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
          <Save size={14} /> {saving ? '...' : t('pk.save')}
        </button>}
      </div>

      {/* KPI */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Kpi label={t('pk.jahresBudget')} value={`€${fmt(totals.jahres_gesamt)}`} sub={`${totals.ma_mit_planung || 0}/${totals.ma_count || 0} MA`} />
        <Kpi label={t('pk.monatsDurchschnitt')} value={`€${fmt(Math.round((totals.jahres_gesamt || 0) / 12))}`} />
        <Kpi label={t('pk.proMa')} value={totals.ma_mit_planung > 0 ? `€${fmt(Math.round((totals.jahres_gesamt || 0) / totals.ma_mit_planung / 12))}` : '—'} sub={t('pk.proMonat')} />
      </div>

      {/* Table */}
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, overflow: 'auto', background: 'var(--bg-card,#fff)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-inset,#F8F9FC)' }}>
              <th style={thStyle}>{t('pk.mitarbeiter')}</th>
              <th style={thStyle}>{t('pk.hWoche')}</th>
              <th style={thStyle}>€/h</th>
              <th style={thStyle}>{t('pk.monatsBrutto')}</th>
              <th style={thStyle}>{t('pk.agPct')}</th>
              <th style={thStyle}>{t('pk.sonderJahres')}</th>
              <th style={{ ...thStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>{t('pk.jahresBruttoCol')}</th>
              <th style={thStyle}>{t('pk.jahresAgCol')}</th>
              <th style={thStyle}>{t('pk.jahresGesamtCol')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const e = edits[r.mitarbeiter_id] || {};
              const mb = e.monats_brutto !== undefined ? e.monats_brutto : r.monats_brutto;
              const agPct = e.ag_nebenkosten_pct !== undefined ? e.ag_nebenkosten_pct : r.ag_nebenkosten_pct;
              const sonder = e.sonderzahlungen_jahres !== undefined ? e.sonderzahlungen_jahres : r.sonderzahlungen_jahres;
              const jahresBrutto = mb != null ? mb * 12 : null;
              const jahresAg = mb != null ? mb * 12 * (agPct || 30) / 100 : null;
              const jahresGesamt = mb != null ? jahresBrutto + jahresAg + (sonder || 0) : null;
              const edited = Object.keys(e).length > 0;

              return (
                <tr key={r.mitarbeiter_id} style={{ borderTop: '1px solid var(--border-card,#E5E7EB)', background: edited ? 'rgba(51,142,220,0.03)' : 'transparent' }}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{r.name}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-tertiary,#6B7280)' }}>{r.wochenstunden_soll}h</td>
                  <td style={{ ...tdStyle, color: 'var(--text-tertiary,#6B7280)' }} title={r.stundensatz == null ? 'Keine Jahres-Lohnkosten gepflegt' : undefined}>{r.stundensatz != null ? `€${r.stundensatz}` : '—'}</td>
                  <td style={tdStyle}>
                    <NumInput value={mb} onChange={v => handleChange(r.mitarbeiter_id, 'monats_brutto', v)} />
                  </td>
                  <td style={tdStyle}>
                    <NumInput value={agPct} onChange={v => handleChange(r.mitarbeiter_id, 'ag_nebenkosten_pct', v)} width={60} />
                  </td>
                  <td style={tdStyle}>
                    <NumInput value={sonder} onChange={v => handleChange(r.mitarbeiter_id, 'sonderzahlungen_jahres', v)} placeholder="0" />
                  </td>
                  <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)', color: 'var(--text-tertiary,#6B7280)' }}>
                    {jahresBrutto != null ? `€${fmt(jahresBrutto)}` : '—'}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-tertiary,#6B7280)' }}>
                    {jahresAg != null ? `€${fmt(jahresAg)}` : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)' }}>
                    {jahresGesamt != null ? `€${fmt(jahresGesamt)}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border-card,#E5E7EB)', background: 'var(--bg-inset,#F8F9FC)', fontWeight: 600 }}>
              <td style={tdStyle}>{t('pk.total')}</td>
              <td colSpan={5} style={tdStyle} />
              <td style={{ ...tdStyle, borderLeft: '2px solid var(--border-card,#E5E7EB)' }}>{/* computed */}</td>
              <td style={tdStyle} />
              <td style={tdStyle}>€{fmt(totals.jahres_gesamt)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#D1FAE5', color: '#065F46', padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 1100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {toast}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════
//  ÜBERSICHT TAB (Trend Chart)
// ═══════════════════════════════════════
function UebersichtTab({ t }) {
  const now = new Date();
  const [jahr, setJahr] = useState(now.getFullYear());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api(`/api/personalkosten/uebersicht?jahr=${jahr}`).then(setData).catch(() => {});
  }, [jahr]);

  useEffect(() => { load(); }, [load]);

  const monate = data?.monate || [];
  const summary = data?.summary || {};

  const chartData = monate.map(m => ({
    name: MONAT_NAMEN[m.monat],
    plan: m.plan_gesamt,
    ist: m.ist_gesamt,
    diff: m.diff
  }));

  const cumData = (() => {
    let cumPlan = 0, cumIst = 0;
    return monate.map(m => {
      cumPlan += m.plan_gesamt;
      cumIst += m.ist_gesamt;
      return { name: MONAT_NAMEN[m.monat], plan_kum: cumPlan, ist_kum: cumIst };
    });
  })();

  return (
    <div>
      {/* Year selector */}
      <div style={{ marginBottom: 16 }}>
        <select value={jahr} onChange={e => setJahr(parseInt(e.target.value))}
          style={{ padding: '8px 12px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 8, background: 'var(--bg-card,#fff)', fontSize: 14, color: 'var(--text-primary,#1A1A2E)' }}>
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* KPI Bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <Kpi label={t('pk.jahresPlan')} value={`€${fmt(summary.jahres_plan)}`} sub={`${summary.plan_abdeckung || 0}/${summary.mitarbeiter_count || 0} MA`} />
        <Kpi label={t('pk.jahresIst')} value={`€${fmt(summary.jahres_ist)}`} />
        <Kpi label={t('pk.jahresDiff')} value={summary.diff != null ? `€${fmt(summary.diff)}` : '—'}
          sub={summary.diff_pct != null ? fmtPct(summary.diff_pct) : null}
          trend={summary.diff > 0 ? 'up' : summary.diff < 0 ? 'down' : null} />
        <Kpi label={t('pk.proMonatAvg')} value={`€${fmt(Math.round((summary.jahres_plan || 0) / 12))}`} />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* PLAN vs IST Bar Chart */}
        <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>{t('pk.planVsIst')}</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `${Math.round(v/1000)}k`} />
              <Tooltip formatter={(v) => `€${fmt(v)}`} />
              <Legend />
              <Bar dataKey="plan" name="PLAN" fill="#338edc" radius={[4,4,0,0]} />
              <Bar dataKey="ist" name="IST" fill="#22D3EE" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Kumulative Linie */}
        <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>{t('pk.kumulativ')}</div>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={cumData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `${Math.round(v/1000)}k`} />
              <Tooltip formatter={(v) => `€${fmt(v)}`} />
              <Legend />
              <Line type="monotone" dataKey="plan_kum" name={t('pk.planKum')} stroke="#338edc" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="ist_kum" name={t('pk.istKum')} stroke="#22D3EE" strokeWidth={2} dot={false} strokeDasharray="5 5" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Table */}
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, overflow: 'auto', background: 'var(--bg-card,#fff)', marginTop: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-inset,#F8F9FC)' }}>
              <th style={thStyle}>{t('pk.monatCol')}</th>
              <th style={thStyle}>PLAN</th>
              <th style={thStyle}>IST</th>
              <th style={thStyle}>{t('pk.diffCol')}</th>
              <th style={thStyle}>%</th>
              <th style={thStyle}>{t('pk.abdeckung')}</th>
            </tr>
          </thead>
          <tbody>
            {monate.map(m => (
              <tr key={m.monat} style={{ borderTop: '1px solid var(--border-card,#E5E7EB)' }}>
                <td style={{ ...tdStyle, fontWeight: 500 }}>{MONAT_NAMEN[m.monat]} {jahr}</td>
                <td style={tdStyle}>€{fmt(m.plan_gesamt)}</td>
                <td style={tdStyle}>{m.ist_gesamt > 0 ? `€${fmt(m.ist_gesamt)}` : '—'}</td>
                <td style={{ ...tdStyle, color: m.diff > 0 ? '#EF4444' : m.diff < 0 ? '#22C55E' : 'var(--text-primary,#1A1A2E)' }}>
                  {m.ist_gesamt > 0 ? `€${fmt(m.diff)}` : '—'}
                </td>
                <td style={{ ...tdStyle, fontSize: 12, color: m.diff_pct > 5 ? '#EF4444' : m.diff_pct < -5 ? '#22C55E' : 'var(--text-tertiary,#6B7280)' }}>
                  {m.diff_pct != null && m.ist_gesamt > 0 ? fmtPct(m.diff_pct) : ''}
                </td>
                <td style={{ ...tdStyle, color: 'var(--text-tertiary,#6B7280)' }}>
                  {m.ma_mit_plan} PLAN / {m.ma_mit_ist} IST
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border-card,#E5E7EB)', background: 'var(--bg-inset,#F8F9FC)', fontWeight: 600 }}>
              <td style={tdStyle}>{t('pk.total')} {jahr}</td>
              <td style={tdStyle}>€{fmt(summary.jahres_plan)}</td>
              <td style={tdStyle}>{summary.jahres_ist > 0 ? `€${fmt(summary.jahres_ist)}` : '—'}</td>
              <td style={{ ...tdStyle, color: summary.diff > 0 ? '#EF4444' : summary.diff < 0 ? '#22C55E' : 'var(--text-primary,#1A1A2E)' }}>
                {summary.jahres_ist > 0 ? `€${fmt(summary.diff)}` : '—'}
              </td>
              <td style={{ ...tdStyle, fontSize: 12, color: summary.diff_pct > 0 ? '#EF4444' : summary.diff_pct < 0 ? '#22C55E' : 'var(--text-tertiary)' }}>
                {summary.diff_pct != null && summary.jahres_ist > 0 ? fmtPct(summary.diff_pct) : ''}
              </td>
              <td style={tdStyle} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}


// ─── NumInput Component ───
function NumInput({ value, onChange, placeholder, width = 90 }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''}
      style={{ width, padding: '4px 8px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 6, fontSize: 13, background: 'var(--bg-card,#fff)', color: 'var(--text-primary,#1A1A2E)', textAlign: 'right' }}
    />
  );
}

// ─── Styles ───
const thStyle = { padding: '10px 12px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary,#6B7280)', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' };


// ═══════════════════════════════════════
//  IMPORT MODAL — Lohnzettel PDF Import
// ═══════════════════════════════════════
function ImportModal({ open, onClose, onImported, t }) {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setStep('upload'); setPreview(null); setError(''); setResult(null); setUploading(false); setConfirming(false); };

  useEffect(() => { if (open) reset(); }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { setError(t('pk.importOnlyPdf')); return; }
    if (file.size > 20 * 1024 * 1024) { setError(t('pk.importFileTooLarge')); return; }

    setError('');
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch('/api/personalkosten/import-lohnzettel', { method: 'POST', credentials: 'include', body: form });
      const data = await res.json();
      if (data.ok) {
        setPreview(data);
        setStep('preview');
      } else {
        setError(data.error || t('pk.importError'));
      }
    } catch {
      setError(t('pk.importError'));
    }
    setUploading(false);
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const file = e.dataTransfer?.files?.[0]; if (file) handleFile(file); };
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const res = await fetch('/api/personalkosten/import-lohnzettel/bestaetigen', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          datei: preview.datei,
          jahr: preview.jahr,
          monat: preview.monat,
          entries: preview.preview.filter(e => e.mitarbeiter_id),
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setResult(data);
        setStep('done');
        if (onImported) onImported();
      } else {
        setError(data.error || t('pk.importError'));
      }
    } catch {
      setError(t('pk.importError'));
    }
    setConfirming(false);
  };

  if (!open) return null;

  const MONAT_NAMEN_FULL = ['', 'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const matchCount = preview?.preview?.filter(e => e.mitarbeiter_id)?.length || 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg-card,#fff)', borderRadius: 16, padding: 0, maxWidth: step === 'preview' ? 900 : 520, width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border-card,#E5E7EB)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-card,#E5E7EB)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileText size={18} style={{ color: '#338edc' }} />
          <div style={{ flex: 1, fontWeight: 600, fontSize: 15, color: 'var(--text-primary,#1A1A2E)' }}>{t('pk.importTitle')}</div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, color: 'var(--text-muted,#9CA3AF)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
          {error && (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* STEP 1: Upload */}
          {step === 'upload' && (
            <div
              onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? '#338edc' : 'var(--border-card,#E5E7EB)'}`,
                borderRadius: 12, padding: '48px 24px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? 'rgba(51,142,220,0.04)' : 'var(--bg-inset,#F8F9FC)',
                transition: 'all 0.15s',
              }}>
              <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              {uploading ? (
                <div style={{ color: '#338edc', fontSize: 14, fontWeight: 500 }}>
                  <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                  {t('pk.importParsing')}
                </div>
              ) : (
                <>
                  <Upload size={32} style={{ color: 'var(--text-muted,#9CA3AF)', marginBottom: 12 }} />
                  <div style={{ fontSize: 14, color: 'var(--text-secondary,#4B5563)', fontWeight: 500 }}>{t('pk.importDropzone')}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted,#9CA3AF)', marginTop: 6 }}>PDF, max 20 MB</div>
                </>
              )}
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === 'preview' && preview && (
            <>
              {/* Stats bar */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                {preview.jahr && preview.monat && (
                  <div style={{ padding: '8px 14px', borderRadius: 8, background: '#EFF6FF', border: '1px solid #DBEAFE', fontSize: 13, color: '#1E40AF', fontWeight: 500 }}>
                    {t('pk.importMonatJahr')}: {MONAT_NAMEN_FULL[preview.monat]} {preview.jahr}
                  </div>
                )}
                <div style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--bg-inset,#F8F9FC)', border: '1px solid var(--border-card,#E5E7EB)', fontSize: 13 }}>
                  {t('pk.importGesamt')}: <strong>{preview.stats.gesamt}</strong>
                </div>
                <div style={{ padding: '8px 14px', borderRadius: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: 13, color: '#065F46' }}>
                  {t('pk.importGematcht')}: <strong>{preview.stats.gematcht}</strong>
                </div>
                {preview.stats.nicht_gematcht > 0 && (
                  <div style={{ padding: '8px 14px', borderRadius: 8, background: '#FEF2F2', border: '1px solid #FECACA', fontSize: 13, color: '#991B1B' }}>
                    {t('pk.importNichtGematcht')}: <strong>{preview.stats.nicht_gematcht}</strong>
                  </div>
                )}
                {preview.stats.bereits_vorhanden > 0 && (
                  <div style={{ padding: '8px 14px', borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 13, color: '#92400E' }}>
                    {t('pk.importBereitsVorhanden')}: <strong>{preview.stats.bereits_vorhanden}</strong>
                  </div>
                )}
              </div>

              {/* Preview table */}
              <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 10, overflow: 'auto', maxHeight: '45vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-inset,#F8F9FC)', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={{ ...thStyle, textAlign: 'left', fontSize: 11 }}>{t('pk.importColRzl')}</th>
                      <th style={{ ...thStyle, textAlign: 'left', fontSize: 11 }}>{t('pk.importColName')}</th>
                      <th style={{ ...thStyle, textAlign: 'left', fontSize: 11 }}>{t('pk.importColMatch')}</th>
                      <th style={{ ...thStyle, fontSize: 11 }}>{t('pk.importColBrutto')}</th>
                      <th style={{ ...thStyle, fontSize: 11 }}>{t('pk.importColNetto')}</th>
                      <th style={{ ...thStyle, fontSize: 11 }}>{t('pk.importColDgKosten')}</th>
                      <th style={{ ...thStyle, fontSize: 11 }}>{t('pk.importColMethode')}</th>
                      <th style={{ ...thStyle, fontSize: 11 }}>{t('pk.importColStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid var(--border-card,#E5E7EB)', background: !row.mitarbeiter_id ? 'rgba(239,68,68,0.03)' : row.bereits_vorhanden ? 'rgba(251,191,36,0.03)' : 'transparent' }}>
                        <td style={{ ...tdStyle, textAlign: 'left', color: 'var(--text-tertiary,#6B7280)' }}>{row.rzl_nr}</td>
                        <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 500 }}>{row.name}</td>
                        <td style={{ ...tdStyle, textAlign: 'left' }}>
                          {row.mitarbeiter_name
                            ? <span style={{ color: '#065F46' }}>{row.mitarbeiter_name}</span>
                            : <span style={{ color: '#991B1B', fontStyle: 'italic' }}>—</span>}
                        </td>
                        <td style={tdStyle}>{row.brutto != null ? `€${fmt(row.brutto)}` : '—'}</td>
                        <td style={tdStyle}>{row.netto != null ? `€${fmt(row.netto)}` : '—'}</td>
                        <td style={tdStyle}>{row.dg_gesamtkosten != null ? `€${fmt(row.dg_gesamtkosten)}` : '—'}</td>
                        <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-muted,#9CA3AF)' }}>{row.match_method || '—'}</td>
                        <td style={tdStyle}>
                          {!row.mitarbeiter_id ? (
                            <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 500, background: '#FEF2F2', color: '#991B1B' }}>
                              {t('pk.importStatusKeinMatch')}
                            </span>
                          ) : row.bereits_vorhanden ? (
                            <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 500, background: '#FFFBEB', color: '#92400E' }}>
                              {t('pk.importStatusUpdate')}
                            </span>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 500, background: '#F0FDF4', color: '#065F46' }}>
                              {t('pk.importStatusNeu')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* STEP 3: Done */}
          {step === 'done' && result && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <CheckCircle size={40} style={{ color: '#22C55E', marginBottom: 12 }} />
              <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 8 }}>
                {result.gesamt} {t('pk.importConfirmCount')} {t('pk.importSuccess')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-tertiary,#6B7280)' }}>
                {result.imported} {t('pk.importNeu')}, {result.updated} {t('pk.importAktualisiert')}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'preview' && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-card,#E5E7EB)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={reset}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', fontSize: 13, cursor: 'pointer', color: 'var(--text-primary,#1A1A2E)' }}>
              {t('common.cancel')}
            </button>
            <button onClick={handleConfirm} disabled={confirming || matchCount === 0}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: matchCount > 0 ? '#338edc' : '#9CA3AF', color: '#fff', fontWeight: 600, fontSize: 13, cursor: matchCount > 0 ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6, opacity: confirming ? 0.6 : 1 }}>
              <Check size={14} /> {confirming ? '...' : `${matchCount} ${t('pk.importConfirmCount')} ${t('pk.importConfirmBtn')}`}
            </button>
          </div>
        )}
        {step === 'done' && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-card,#E5E7EB)', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onClose}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#338edc', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════
//  FORECAST TAB — 3-Monats-Prognose
// ═══════════════════════════════════════
function ForecastTab({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/personalkosten/forecast?monate=3')
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: 'var(--text-tertiary,#6B7280)', padding: 40 }}>{t('pk.loading') || 'Lädt...'}</div>;
  if (!data) return <div style={{ color: 'var(--text-tertiary,#6B7280)', padding: 40 }}>{t('pk.noData') || 'Keine Daten'}</div>;

  const chartData = [
    ...data.ist_vergleich.map(m => ({
      name: MONAT_NAMEN[parseInt(m.monat.split('-')[1])] + ' (IST)',
      fix: m.gesamt, flex: 0, ues: 0, gesamt: m.gesamt, typ: 'ist',
    })),
    ...data.forecast.map(m => ({
      name: MONAT_NAMEN[parseInt(m.monat.split('-')[1])] + ' (FC)',
      fix: m.fix_kosten, flex: m.flex_kosten, ues: m.ues_kosten, gesamt: m.gesamt, typ: 'forecast',
    })),
  ];

  const fcGesamt = data.forecast.reduce((s, m) => s + m.gesamt, 0);
  const istGesamt = data.ist_vergleich.reduce((s, m) => s + m.gesamt, 0);
  const avgFc = Math.round(fcGesamt / (data.forecast.length || 1));
  const avgIst = Math.round(istGesamt / (data.ist_vergleich.length || 1));

  return (
    <div>
      {/* KPI row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Kpi label={t('pk.fcGesamtLabel') || 'Forecast Gesamt (3M)'} value={`€ ${fmt(fcGesamt)}`} />
        <Kpi label={t('pk.fcAvgLabel') || 'Ø Monat (Forecast)'} value={`€ ${fmt(avgFc)}`} />
        <Kpi label={t('pk.istAvgLabel') || 'Ø Monat (IST letzte 3M)'} value={`€ ${fmt(avgIst)}`}
          sub={avgIst > 0 ? fmtPct((avgFc - avgIst) / avgIst * 100) : '—'}
          trend={avgFc > avgIst ? 'up' : 'down'} />
        <Kpi label={t('pk.fcMaCount') || 'Aktive MA'} value={data.forecast[0]?.ma_count || 0} />
      </div>

      {/* Stacked bar chart */}
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)', marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 16 }}>
          {t('pk.fcChartTitle') || 'IST (letzte 3M) vs Forecast (nächste 3M)'}
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card,#E5E7EB)" />
            <XAxis dataKey="name" fontSize={12} tick={{ fill: 'var(--text-tertiary,#6B7280)' }} />
            <YAxis fontSize={12} tick={{ fill: 'var(--text-tertiary,#6B7280)' }} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => `€ ${fmt(v)}`} />
            <Legend />
            <Bar dataKey="fix" name={t('pk.fcFix') || 'Fixkosten'} stackId="a" fill="#338edc" radius={[0, 0, 0, 0]} />
            <Bar dataKey="flex" name={t('pk.fcFlex') || 'Flex-Worker'} stackId="a" fill="#22D3EE" />
            <Bar dataKey="ues" name={t('pk.fcUes') || 'Überstunden'} stackId="a" fill="#6366F1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detail table */}
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, padding: 20, background: 'var(--bg-card,#fff)', marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary,#1A1A2E)', marginBottom: 12 }}>
          {t('pk.fcDetail') || 'Forecast-Detail'}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-card,#E5E7EB)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>{t('pk.monat') || 'Monat'}</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>{t('pk.arbeitstage') || 'AT'}</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>{t('pk.fcFix') || 'Fix'}</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>{t('pk.fcFlex') || 'Flex'}</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>{t('pk.fcUes') || 'ÜS'}</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>{t('pk.gesamt') || 'Gesamt'}</th>
            </tr>
          </thead>
          <tbody>
            {data.forecast.map(m => (
              <tr key={m.monat} style={{ borderBottom: '1px solid var(--border-card,#E5E7EB)' }}>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{MONAT_NAMEN[parseInt(m.monat.split('-')[1])] + ' ' + m.monat.split('-')[0]}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{m.arbeitstage}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>€ {fmt(m.fix_kosten)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>€ {fmt(m.flex_kosten)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>€ {fmt(m.ues_kosten)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>€ {fmt(m.gesamt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Annahmen */}
      <div style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)', display: 'flex', alignItems: 'flex-start', gap: 6, padding: '0 4px' }}>
        <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        {data.annahmen}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════
export default function PersonalkostenPage() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('monitor');
  const [importOpen, setImportOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const tabs = [
    { key: 'monitor', labelKey: 'pk.tabMonitor' },
    { key: 'monat', labelKey: 'pk.tabMonat' },
    { key: 'jahresplanung', labelKey: 'pk.tabJahresplanung' },
    { key: 'uebersicht', labelKey: 'pk.tabUebersicht' },
    { key: 'forecast', labelKey: 'pk.tabForecast' },
    { key: 'einstellungen', labelKey: 'modulSettings.title' },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary,#1A1A2E)', margin: 0, flex: 1 }}>{t('pk.title')}</h1>
        <button onClick={() => setImportOpen(true)}
          style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', fontSize: 13, cursor: 'pointer', color: 'var(--text-primary,#1A1A2E)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
          <Upload size={14} /> {t('pk.importBtn')}
        </button>
      </div>

      <TabBar tabs={tabs} active={tab} onChange={setTab} t={t} />

      {tab === 'monitor' && <MonitorTab key={reloadKey} t={t} />}
      {tab === 'monat' && <MonatTab key={reloadKey} t={t} />}
      {tab === 'jahresplanung' && <JahresplanungTab t={t} />}
      {tab === 'uebersicht' && <UebersichtTab key={reloadKey} t={t} />}
      {tab === 'forecast' && <ForecastTab t={t} />}
      {tab === 'einstellungen' && <ModulEinstellungen kategorien={['personalkosten']} />}

      <ImportModal open={importOpen} onClose={() => setImportOpen(false)}
        onImported={() => setReloadKey(k => k + 1)} t={t} />
    </div>
  );
}
